/**
 * `GhElement` - the base class every element in this app extends, island or component.
 *
 * ## Shadow DOM is the default, and that is deliberate (round 65, owner call)
 *
 * This class does NOT override `createRenderRoot`, so it inherits Lit's default: an open
 * shadow root. The first four islands each opted into light DOM because the app's styling
 * is one global stylesheet and a shadow root would have walled them off from it - a fair
 * call for a PAGE mid-migration, but the wrong default for the codebase, because it means
 * every new component is born unencapsulated and the eventual move gets more expensive with
 * each one. So the default flipped: **shadow unless a subclass says otherwise**, and light
 * DOM is now an explicit, greppable opt-out (`createRenderRoot() { return this; }`) that
 * each page sheds when its CSS moves into the component.
 *
 * The thing that makes this safe is a fact about CSS custom properties: **they inherit
 * THROUGH shadow boundaries.** Every color, radius and spacing token in this app is a
 * `--var` on `:root`, and the theme (light/dark, accent, font) is applied by swapping those
 * - so a shadow-rooted component still themes correctly, automatically, with no plumbing.
 * What does NOT cross the boundary is ordinary selector-based CSS (`.stat-tile { … }`), and
 * that is exactly the coupling worth cutting: a component's internals stop being reachable
 * by any page's stylesheet, so nobody can reach in and restyle them by accident.
 *
 * A component that goes shadow therefore owns its own `static styles`. When the last
 * legacy caller of a given block of `public/style.css` dies, that block dies with it.
 *
 * ## The other half: the `localeStore` subscription
 *
 * `t()` resolves at RENDER time, so an element that has already painted holds text that no
 * `data-i18n` attribute sweep can reach (`applyStaticI18n` only rewrites the static
 * chrome). Subscribing on connect and re-rendering on a change is what makes a live
 * language flip reach a rendered element - including one whose page section is hidden at
 * the time, which is most of them. Forgetting it produces the nastiest kind of bug:
 * correct on first paint, stale forever after, invisible to anyone testing in one language.
 * In the base class, an element has to opt OUT of correctness rather than remember to opt in.
 *
 * Subclasses overriding `connectedCallback`/`disconnectedCallback` must call `super` - Lit
 * already requires that of them.
 */
import { LitElement } from "lit";
import { localeStore } from "../i18n.ts";
import { bindTooltipsInShadowRoot } from "../tooltip.ts";
import { syncModalFocus } from "../a11y.ts";

export class GhElement extends LitElement {
	#unsubscribeLocale: (() => void) | undefined;

	constructor() {
		super();
		// ── The modal focus manager's one hook (§R) ────────────────────────────────────────────────
		// A modal in this app is rendered from island state, so the only moment one can appear or
		// disappear is a render, and `a11y.ts` has to hear about it to move focus in, contain it, and
		// put it back afterwards (WCAG 2.4.3 / 2.1.2).
		//
		// ⚠ **A CONTROLLER rather than an `updated()` override, and that is the point of it.** Eleven
		// islands already override `updated()` for their own imperative work; a twelfth that forgot
		// `super.updated()` would be a page whose dialogs are silently untrapped, with every gate
		// green and nothing on screen looking wrong. Lit calls `hostUpdated()` from its own internals,
		// before and independently of any `updated()` a subclass writes - so there is nothing here for
		// a future island to forget. **A hook that exists for correctness rather than for a feature
		// belongs where a subclass cannot skip it.**
		this.addController({
			hostUpdated: () => {
				if (this.renderRoot instanceof ShadowRoot) syncModalFocus(this.renderRoot);
			}
		});
	}

	connectedCallback(): void {
		super.connectedCallback();
		this.#unsubscribeLocale = localeStore.subscribe(() => this.requestUpdate());
		// ⚠ Tooltips are delegated, and a delegated listener on `document` is BLIND to a pointer
		// moving between two nodes of the same shadow tree: the DOM spec truncates a mouseover whose
		// target and relatedTarget share a root, so the event never leaves this element. Every
		// `[data-tooltip]` in the app sits inside one of these roots. See `tooltip.ts` for the long
		// form; binding here is what makes a hover anywhere inside an island reach the seam at all.
		// Lit has created the render root by now, and the call is idempotent per root.
		if (this.renderRoot instanceof ShadowRoot) bindTooltipsInShadowRoot(this.renderRoot);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.#unsubscribeLocale?.();
		this.#unsubscribeLocale = undefined;
	}
}
